<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { resolveRelease, searchCatalog } from "../api";
import type { CatalogItem, Digest } from "../types";

const route = useRoute();
const router = useRouter();
const query = ref(typeof route.query.q === "string" ? route.query.q : "");
const release = ref<Digest>();
const items = ref<CatalogItem[]>([]);
const loading = ref(true);
const nextCursor = ref<string | null>(null);
const grouped = computed(() => Object.groupBy(items.value, (item) => item.kind));
const hasQuery = computed(() => query.value.trim().length > 0);
let timer: ReturnType<typeof setTimeout> | undefined;

function target(item: CatalogItem) {
  const plural =
    item.kind === "chip" ? "chips" : item.kind === "peripheral" ? "peripherals" : "registers";
  return {
    path: `/releases/${release.value}/${plural}/${item.entity_id}`,
    query: item.kind === "chip" ? {} : { revision: item.revision_id },
  };
}
async function run(append = false) {
  loading.value = true;
  release.value ??= await resolveRelease();
  if (!hasQuery.value) {
    items.value = [];
    nextCursor.value = null;
    loading.value = false;
    return;
  }
  const page = await searchCatalog(
    release.value,
    query.value.trim(),
    append ? (nextCursor.value ?? undefined) : undefined,
  );
  items.value = append ? [...items.value, ...page.items] : page.items;
  nextCursor.value = page.next_cursor;
  loading.value = false;
}
function schedule() {
  clearTimeout(timer);
  timer = setTimeout(() => {
    void router.replace({ query: query.value ? { q: query.value } : {} });
    void run();
  }, 180);
}
watch(query, schedule);
onMounted(() => void run());
</script>
<template>
  <section class="hero-search">
    <p class="eyebrow">Versioned hardware knowledge</p>
    <h1>Find the fact.<br /><em>Trace the source.</em></h1>
    <p class="lede">
      Explore chips, peripheral IP, registers, fields, memory maps, and exact artifacts from one
      canonical release.
    </p>
    <label class="search-box"
      ><span class="search-icon" aria-hidden="true">⌕</span
      ><span class="sr-only">Search hardware catalog</span
      ><input
        v-model="query"
        type="search"
        autocomplete="off"
        placeholder="Search part number, peripheral, register, or field…"
      /><kbd>⌘ K</kbd></label
    >
    <div class="release-line">
      <span class="live-dot"></span><span>Stable release</span
      ><code>{{ release?.slice(0, 22) }}…</code>
    </div>
  </section>
  <section class="results-section" aria-live="polite">
    <div class="section-heading">
      <div>
        <p class="eyebrow">Catalog</p>
        <h2>
          {{
            loading
              ? "Loading catalog…"
              : hasQuery
                ? `${items.length} results for “${query.trim()}”`
                : "Search the catalog"
          }}
        </h2>
      </div>
      <div class="filter-chips">
        <button class="active">All</button><button>Chips</button><button>Peripherals</button
        ><button>Registers</button>
      </div>
    </div>
    <div v-if="items.length" class="result-grid">
      <RouterLink
        v-for="item in items"
        :key="`${item.kind}${item.entity_id}${item.revision_id}`"
        :to="target(item)"
        class="result-card"
      >
        <div>
          <span class="kind" :data-kind="item.kind">{{
            item.kind === "chip" ? "SoC" : item.kind
          }}</span
          ><code>{{ item.revision_id.slice(7, 15) }}</code>
        </div>
        <h3>{{ item.name }}</h3>
        <p class="subtitle">{{ item.subtitle }}</p>
        <p>{{ item.description }}</p>
        <span class="open">Open record <b>→</b></span>
      </RouterLink>
    </div>
    <p v-else-if="!loading" class="empty">
      {{
        hasQuery
          ? "No records match this query. Try a part alias, peripheral type, or register name."
          : "Enter a chip, peripheral, register, or field name to begin."
      }}
    </p>
    <button v-if="nextCursor" class="secondary-button" @click="run(true)">Load more</button>
    <span class="sr-only">{{ Object.keys(grouped).length }} result types</span>
  </section>
</template>
